import { Body, Controller, Post, UploadedFiles, UseInterceptors } from "@nestjs/common";
import { FilesInterceptor } from "@nestjs/platform-express";
import { ApiBody, ApiConsumes, ApiTags } from "@nestjs/swagger";
import { CommonResponseModel, SkechersPoNumberHistory } from "@project-management-system/shared-models";
import * as fs from 'fs';
import { diskStorage } from "multer";
import { ApplicationExceptionHandler } from "packages/libs/backend-utils/src/exception-handling/application-exception-handler";
import { extname, join } from "path";
import { FileUploadMainDto } from "./dto/file-upload.dto";
import { SKechersOrderDetailsReq } from "./dto/skechers-orders-details.dto";
import { SkechersService } from "./skechers.service";

@ApiTags("/skechers")
@Controller("/skechers-orders")
export class SkechersController {
    constructor(
        private readonly Service: SkechersService,
        private readonly applicationExeptionhandler: ApplicationExceptionHandler,

    ) { }

    @ApiConsumes('multipart/form-data')
    @ApiBody({ type: FileUploadMainDto })
    @Post('/fileUploadSkechers')
    @UseInterceptors(FilesInterceptor('files', 10, {
        storage: diskStorage({
            destination: (req, file, callback) => {
                const destinationPath = join(__dirname, '../../../../', `pdf-files`);
                try {
                    fs.mkdirSync(destinationPath, { recursive: true });
                    callback(null, destinationPath);
                } catch (error) {
                    callback(error, null);
                }
            },
            filename: (req, file, callback) => {
                const name = file.originalname.split('.')[0];
                const fileExtName = extname(file.originalname);
                const randomName = Array(4)
                    .fill(null)
                    .map(() => Math.round(Math.random() * 16).toString(16))
                    .join('');
                callback(null, `${name} - ${randomName}${fileExtName}`);
            },
        }),
        fileFilter: (req, file, callback) => {
            if (!file.originalname.match(/\.(PDF|pdf)$/)) {
                return callback(new Error('Only pdf files are allowed!'), false);
            }
            callback(null, true);
        },
    }))
    async fileUploadSkechers(@UploadedFiles() files: Express.Multer.File[], extractedData?: any[]): Promise<CommonResponseModel> {
        try {
            return await this.Service.fileUploadSkechers(files);
        } catch (err) {
            console.error('Error during file upload:', err);
            throw new Error('File upload failed');
        }
    }

    @Post("/saveSkechersOrdersData")
    async saveSkechersOrdersData(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.saveSkechersOrdersData(req);
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error);
        }
    }

    @Post('/fileUpload')
    @UseInterceptors(FilesInterceptor('file', 10, {
        storage: diskStorage({
            destination: (req, file, callback) => {
                const destinationPath = join(__dirname, '../../../../', `upload_files`)
                try {
                    fs.mkdirSync(destinationPath, { recursive: true });
                    callback(null, destinationPath);
                } catch (error) {
                    callback(error, null);
                }
            },
            filename: (req, file, callback) => {
                const name = file.originalname.split('.')[0];
                const fileExtName = extname(file.originalname);
                const randomName = Array(4)
                    .fill(null)
                    .map(() => Math.round(Math.random() * 16).toString(16))
                    .join('');
                callback(null, `${name}-${randomName}${fileExtName}`);
            },
        }),
        fileFilter: (req, file, callback) => {
            if (!file.originalname.match(/\.(xlsx|xls|pdf|jpg|png|jpeg|doc|PDF|xml|XML)$/)) {
                return callback(new Error('Only xlsx,xls,pdf, jpg, png, doc,xml,XML jpeg files are allowed!'), false);
            }
            callback(null, true);
        },
    }))
    async updateStylePath(@UploadedFiles() file: File[], @Body() req: any): Promise<CommonResponseModel> {
        try {

            return await this.Service.updatePath(file, req.jsonData, req.poNumber)
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error);
        }
    }

    @Post('/getPdfFileInfo')
    @ApiBody({ type: SkechersPoNumberHistory })
    async getPdfFileInfo(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return this.Service.getPdfFileInfo(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getPoNumber')
    async getPoNumber(): Promise<CommonResponseModel> {
        try {
            return this.Service.getPoNumber();
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getSkechersOrderData')
    async getSkechersOrderData(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.getSkechersOrderData(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err)
        }
    }

    @Post('/saveAddressInfo')
    async saveAddressInfo(@Body() data: any): Promise<CommonResponseModel> {
        try {
            return this.Service.saveAddressInfo(data);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getAddressInfo')
    async getAddressInfo(): Promise<CommonResponseModel> {
        try {
            return this.Service.getAddressInfo();
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getAddressInfoByCountry')
    async getAddressInfoByCountry(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return this.Service.saveSkechersOrdersData(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getSkechersOrderAcceptanceData')
    async getSkechersOrderAcceptanceData(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.getSkechersOrderAcceptanceData(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err)
        }
    }

    @Post('/skechersCoLineCreationReq')
    async skechersCoLineCreationReq(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.skechersCoLineCreationReq(req)
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }

    @Post('/getCoPoNumber')
    async getCoPoNumber(@Body() req?: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.getCoPoNumber()
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }


    @Post('/getItem')
    async getItem(@Body() req?: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.getItem()
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }


    @Post('/updateItemNo')
    async updateItemNo(@Body() req?: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.updateItemNo(req)
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }


    @Post('/getCentricCoLine')
    async getCentricCoLine(@Body() req?: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.getCentricCoLine(req)
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }

    @Post('/deleteCoLine')
    async deleteCoLine(@Body() req?: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.deleteCoLine(req)
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }

    @Post('/updateStatusInOrder')
    async updateStatusInOrder(@Body() req?: any): Promise<CommonResponseModel> {
        try {
            return await this.Service.updateStatusInOrder(req)
        } catch (error) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, error)
        }
    }

    @Post('/saveColorInfo')
    async saveColorInfo(@Body() data: any): Promise<CommonResponseModel> {
        try {
            return this.Service.saveColorInfo(data);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getColorInfo')
    async getColorInfo(): Promise<CommonResponseModel> {
        try {
            return this.Service.getColorInfo();
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/saveSizeInfo')
    async saveSizeInfo(@Body() data: any): Promise<CommonResponseModel> {
        try {
            return this.Service.saveSizeInfo(data);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getSizeInfo')
    async getSizeInfo(): Promise<CommonResponseModel> {
        try {
            return this.Service.getSizeInfo();
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/skechersPdfBot')
    async skechersPdfBot(): Promise<CommonResponseModel> {
        try {
            return this.Service.skechersPdfBot();
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getOrderdataForCOline')
    @ApiBody({ type: SKechersOrderDetailsReq })
    async getOrderdataForCOline(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return this.Service.getOrderdataForCOline(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/createCOline')
    async createCOline(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return this.Service.createCOline(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }

    @Post('/getOrderComparsionData')
    async getOrderComparsionData(@Body() req: any): Promise<CommonResponseModel> {
        try {
            return this.Service.getOrderComparsionData(req);
        } catch (err) {
            return this.applicationExeptionhandler.returnException(CommonResponseModel, err);
        }
    }
    

}